<script lang="ts">
  import { onMount } from "svelte"
  import Layout from "$app/Layouts/Plan.svelte"
  import Sortable from "sortablejs"
  import FlywheelEntry from "./FlywheelEntry.svelte"
  import { xhr, toast, wait, sortElementsForSortable } from "$app/utils"
  import FlywheelForm from "./FlywheelForm.svelte"
  import FlywheelView from "./FlywheelView.svelte"
  import FlywheelEntryForm from "./FlywheelEntryForm.svelte"
  import { SquarePen, InfoIcon } from "lucide-svelte"
  import { appStore } from "$app/store.svelte"

  import flywheel_img from "@images/flywheel/flywheel.png"

  let props = $props()
  let plan: PlanType = props.plan
  let flywheel: FlywheelType = $state(props.flywheel)
  let flywheel_entries: FlywheelEntryType[] = $state(props.flywheel_entries)

  let mode = $state("edit")
  let can_edit = $state(false)
  let editTheme = $state(false)

  const newFlywheelEntryData = {
    title: "",
    description: ""
  } as FlywheelEntryType

  function initializeFlywheelEntries(
    flywheel_entry: FlywheelEntryType
  ): FlywheelEntryType {
    return {
      ...flywheel_entry,
      _popover: false,
      _selected: false,
      _edit: false
    }
  }

  let saving = $state(false)
  let newFlywheelEntry = $state(false)

  function updateFlywheel(data) {
    flywheel = data
    editTheme = false
  }

  // Delete flywheel item
  async function deleteFlywheelEntry(
    flywheel_entry: FlywheelEntryType
  ): Promise<void> {
    flywheel_entry._selected = true

    try {
      xhr.delete(
        `/plans/${appStore.plan.id}/flywheel_entries/${flywheel_entry.id}`
      )
    } catch (e) {
      toast("There was an error deleting the flywheel entry", "error")
    } finally {
      toast(`Flywheel Entry was deleted.`, "success")
      flywheel_entry._selected = false
      flywheel_entries = flywheel_entries.filter(
        (d) => d.id !== flywheel_entry.id
      )
    }
  }

  let sortableSection: HTMLElement = $state(null)

  function setSortable(): void {
    let timeoutHandler: any = null
    const delay = 20
    let elem: HTMLElement

    let scrollFn: Function

    if (!sortableSection) return

    const sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "flywheel_entries",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        if (e.newIndex == e.oldIndex) return
        sort.options.sort = false // Disable sorting while saving
        try {
          await saveSortOrder(
            sortElementsForSortable(flywheel_entries, e.newIndex, e.oldIndex)
          )
        } catch (e) {
        } finally {
          sort.options.sort = true
        }
      },
      onStart: (evt: EventSortType) => {
        const contHeight = evt.srcElement.clientHeight
        const itemHeight = evt.item.clientHeight

        scrollFn = evt.item.ondrag = (mevt: MouseEvent) => {
          if (timeoutHandler == null) {
            timeoutHandler = setTimeout(function () {
              const offsetClientY = mevt.clientY + 20

              if (offsetClientY > contHeight) {
                elem.scrollBy(0, (offsetClientY / 40) * 5)
              } else if (mevt.clientY - itemHeight < 0) {
                elem.scrollBy(
                  0,
                  (Math.abs(mevt.clientY - itemHeight) / 30) * -10
                )
              }
              timeoutHandler = null
            }, delay)
          }
        }
      }
    })

    elem = sort.el
  }

  async function saveSortOrder(
    newFlywheelEntries: FlywheelEntryType[]
  ): Promise<void> {
    try {
      let resp = await xhr.post(
        `/plans/${appStore.plan.id}/flywheel/sort.json`,
        {
          flywheel_id: appStore.flywheel.id,
          flywheel_entries_ids: newFlywheelEntries.map((st) => st.id)
        }
      )
      flywheel_entries = resp.data.flywheel_entries
    } catch (e) {
      toast("There was an error updating the sort order", "error")
      let tempFlywheelItems = [...flywheel_entries]
      flywheel_entries = []
      await wait(30)
      flywheel_entries = tempFlywheelItems
    }
  }

  async function createFlywheelEntry(flywheel_entry: FlywheelEntryType) {
    newFlywheelEntry = false

    flywheel_entry._selected = false
    flywheel_entries = [...flywheel_entries, flywheel_entry]

    await wait(flywheel_entries.length * 5)
    setSortable()
  }

  async function updateMode(new_mode) {
    mode = new_mode
    await wait(30)
    setSortable()
  }

  onMount(async () => {
    appStore.plan = plan
    appStore.flywheel = flywheel

    flywheel_entries.map((d) => initializeFlywheelEntries(d))

    if (flywheel_entries.length >= 4) {
      mode = "view"
    }

    if (
      appStore.current_user.role === "admin" ||
      appStore.current_user.role === "leader"
    ) {
      can_edit = true
    }

    setSortable()
  })
</script>

<Layout {...props}>
  <div class="max-w-7xl mx-auto sm:px-6 lg:px-8 pb-8 py-6">
    <div class="divide-y divide-gray-200 rounded-lg bg-white shadow">
      <div class="px-4 py-5 sm:px-6 flex justify-between items-center">
        <p class="text-2xl">FlyWheel</p>

        {#if can_edit && flywheel.enabled && flywheel_entries.length >= 4}
          <div class="isolate inline-flex rounded-md shadow-sm">
            <button
              type="button"
              onclick={() => updateMode("edit")}
              class="relative inline-flex items-center rounded-l-md px-4 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 focus:z-10 {mode ===
              'edit'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-50 text-blue-800 hover:bg-blue-100'}">Edit</button
            >
            <button
              type="button"
              onclick={() => updateMode("view")}
              class="relative -ml-px inline-flex items-center rounded-r-md px-4 py-2 text-sm font-semibold text-gray-900 ring-1 ring-inset ring-gray-300 focus:z-10 {mode ===
              'view'
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-50 text-blue-800 hover:bg-blue-100'}">View</button
            >
          </div>
        {/if}
      </div>
      <div class="px-4 py-5 sm:p-6">
        {#if mode == "view"}
          <FlywheelView {flywheel} {flywheel_entries} />
        {:else if can_edit && flywheel.enabled && !editTheme}
          <div class="flex flex-col max-w-2xl mx-auto py-6">
            <div
              class="border rounded-t border-indigo-200 bg-indigo-50 p-6 relative"
            >
              <button
                class="absolute top-2 right-2 bg-blue-600 py-1 px-3 border border-blue-700 rounded text-blue-100 text-xs font-bold flex items-center hover:bg-blue-700 hover:shadow-lg"
                onclick={() => (editTheme = true)}
                ><SquarePen class="w-4 h-4 mr-1" />Edit Theme</button
              >
              <p class="text-xs text-center text-zinc-500">Central Theme</p>
              <h2 class="text-3xl text-center mt-2 text-indigo-600 font-bold">
                {flywheel.title}
              </h2>
              {#if flywheel.description?.length > 0}
                <p class="text-xs text-center text-zinc-500 mt-6">
                  Description
                </p>
                <div
                  class="ProseMirror max-w-lg mx-auto border border-indigo-200 p-4 mt-2 rounded bg-gray-50"
                >
                  {@html flywheel.description}
                </div>
              {:else}
                <p class="text-gray-600 italic mt-2 text-center">
                  No Description Entered
                </p>
              {/if}
            </div>
            <span
              class="left-4 top-4 -ml-px h-full w-0.5 bg-gray-200"
              aria-hidden="true"
            ></span>
            <div class="rounded-b border border-blue-200 p-6">
              <div class="flex justify-center items-center">
                <h3 class="text-xl font-bold text-center">Flywheel Entries</h3>
                {#if flywheel_entries.length >= 4}
                  <button
                    onclick={() => updateMode("view")}
                    class="ml-2 py-1 px-3 bg-gray-100-100 text-gray-800 border rounded my-3 text-sm hover:bg-blue-100 hover:shadow-lg"
                  >
                    View Flywheel
                  </button>
                {/if}
              </div>
              {#if flywheel_entries.length >= 1 && flywheel_entries.length < 4}
                <p
                  class="py-2 px-4 bg-orange-100 text-orange-500 border rounded my-3"
                >
                  Please add between 4 - 6 entries to view your flywheel.
                </p>
              {/if}
              <div class="w-full mt-4">
                <ul
                  class="my-2 hover:cursor-move"
                  bind:this={sortableSection}
                  role="list"
                >
                  {#if newFlywheelEntry}
                    <li>
                      <FlywheelEntryForm
                        flywheel_entry={newFlywheelEntryData}
                        {saving}
                        oncancel={() => (newFlywheelEntry = false)}
                        oncreate={(data) => createFlywheelEntry(data)}
                      />
                    </li>
                  {:else}
                    {#each flywheel_entries as flywheel_entry, idx (`${idx}-${flywheel_entry.id}`)}
                      <li class="mb-2 border border-gray-200">
                        <FlywheelEntry
                          {flywheel_entry}
                          ondelete={(data) => deleteFlywheelEntry(data)}
                        />
                      </li>
                    {:else}
                      <li
                        class="mb-2 border border-gray-200 p-4 text-center text-orange-600"
                      >
                        You currently have no flywheel entries. Get started with
                        adding your first and once you have 4 entries you will
                        be able to view your Flywheel.
                      </li>
                    {/each}
                  {/if}
                </ul>
              </div>

              {#if !newFlywheelEntry}
                <div class="w-full text-center mt-8 pb-2">
                  {#if flywheel_entries.length < 6}
                    <button
                      class="btn btn-outline text-base !bg-orange-50 hover:bg-orange-100 hover:shadow-lg"
                      onclick={() => {
                        newFlywheelEntry = true
                      }}>Add New Flywheel Entry</button
                    >
                  {:else}
                    <span
                      class="text-red-700 text-sm border border-red-200 rounded py-2 px-4"
                      >You have created a maximum of 6 entries</span
                    >
                  {/if}
                </div>
              {/if}
            </div>
          </div>
        {:else if !can_edit && mode == "edit"}
          <div class="py-12 flex items-center justify-center">
            <div class="p-6 bg-gray-50 text-lg italic">
              The FlyWheel has not yet been created by your Leadership Team.
            </div>
          </div>
        {:else if editTheme}
          <div
            class="flex flex-col max-w-2xl mx-auto p-6 border border-indigo-200 rounded"
          >
            <div class="flex justify-center items-center">
              <h2 class="text-2xl font-bold text-indigo-600">
                Edit Your Flywheel Theme
              </h2>
              <div class="flex items-center ml-4">
                <img src={flywheel_img} class="w-20 h-20" alt="Flywheel" />
              </div>
            </div>
            <FlywheelForm
              {flywheel}
              onupdate={(data) => {
                updateFlywheel(data)
              }}
            />
          </div>
        {:else}
          <div
            class="flex flex-col max-w-2xl mx-auto p-6 border border-indigo-200 rounded"
          >
            <h2 class="text-2xl font-bold text-center text-indigo-600">
              Set up your Flywheel
            </h2>
            <div class="flex items-center mt-4 py-3 px-2">
              <img src={flywheel_img} class="w-24 h-24" alt="Flywheel" />
              <p class="text-lg ml-2 text-indigo-900">
                The Flywheel effect consists of a central theme which is placed
                at the center of the wheel, and then 4 - 6 entries which will
                flow around this theme in a circular pattern.
              </p>
            </div>
            <div
              class="flex justify-center items-center mx-auto max-w-lg bg-indigo-50 rounded-sm shadow mt-4 p-4"
            >
              <InfoIcon class="w-12 h-12 text-indigo-800" />
              <p class="text-base bold text-indigo-700 ml-4">
                Get started by entering your Central Theme and an optional
                description. After clicking Create Flywheel you can add your
                entries.
              </p>
            </div>

            <FlywheelForm {flywheel} onupdate={updateFlywheel} />
          </div>
        {/if}
      </div>
    </div>
  </div>
</Layout>
